import { SceneEditor } from '@zakeke/zakeke-3d-core';
import { Checkbox } from '../../../../../../widgets/checkbox';
import { Dialog } from '../../../../../../widgets/dialog';
import { FormControl } from '../../../../../../widgets/formControl';
import { Icon } from '../../../../../../widgets/icon';
import { NotificationBanner } from '../../../../../../widgets/notificationBanner';
import { showErrorNotification } from '../../../../../../widgets/notificationToaster';
import React from 'react';
import { fetchQuery, useLazyLoadQuery, useRelayEnvironment } from 'react-relay';
import Loader from 'src/components/widgets/loader';
import { T } from 'src/shared/helpers';
import theme from 'src/shared/theme';

import Play from 'src/shared/icons/play-solid.svg';
import styled from 'styled-components';
import {
    AttributePreviewAction,
    AttributePreviewItems,
    AttributePreviewOption,
    ComposerAttributeNode,
    ComposerMaterialsColorType
} from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import ComposerPreview from '../../../configurator-components/composer-editors/composerPreview';
import { composerQueriesCheckAiLimitQuery } from '../__generated__/composerQueriesCheckAiLimitQuery.graphql';
import { composerQueriesGenerateAiAttributesQuery } from '../__generated__/composerQueriesGenerateAiAttributesQuery.graphql';
import { PromptAiArea } from '../components/composerMultiPromptElement';
import { composerCheckAiLimitQuery, composerGenerateAiAttributes } from '../composerQueries';
import { getCapitalizedFormattedName } from '../helpers/composerHelpers';
import { ComposerAttributeNodeAdapter } from '../helpers/composerNodesAdapter';

enum ComposerGenerateAttributeSteps {
    Initial,
    WaitingGeneration,
    ComposerPreview
}

interface ComposerGenerateAIAttributeDialogProps {
    meshList?: BABYLON.AbstractMesh[];
    materialList?: BABYLON.Material[];
    onConfirm: (attributes: ComposerAttributeNode[]) => void;
    onClose: () => void;
    sceneEditor: SceneEditor | null | undefined;
}
const ComposerGenerateAIAttributeDialogWindow = styled(Dialog)`
    max-width: 800px;
`;
const Title = styled.h3`
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
`;
const CenteredContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px;
`;
const StepContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 16px;
`;
interface AttributePreviewCardProps {
    attributeTitle: string;
    selected: boolean;
    optionsList: AttributePreviewOption[];
    onOptionClick?: (option: AttributePreviewOption) => void;
    onSelected?: (selected: boolean) => void;
}

const AttributePreviewCardContainer = styled.div<{ $selected: boolean }>`
    display: flex;
    flex-direction: column;
    border: 1px solid ${(props) => (props.$selected ? theme.app.secondary : theme.app.borderColor)};
    border-radius: 4px;
    margin-bottom: 8px;
    position: relative;
`;

const AttributePreviewCardSelectWrapper = styled.div`
    padding: 8px;
    position: absolute;
    right: -10px;
    top: 2px;
    display: flex;
    align-items: center;
    gap: 4px;
`;

const AttributePreviewCardHeader = styled.div`
    display: flex;
    align-items: center;
    padding: 8px 12px;
    cursor: pointer;
    background-color: ${theme.app.backgroundColor};
    border-bottom: 1px solid ${theme.app.borderColor};
`;

const AttributePreviewCardTitle = styled.div`
    font-weight: 500;
`;

const AttributePreviewCardOptions = styled.div<{ isOpen: boolean }>`
    max-height: ${({ isOpen }) => (isOpen ? '500px' : '0')};
    overflow: hidden;
    transition: all 0.3s ease;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
`;

const AttributePreviewCardOption = styled.div`
    margin: 8px;
    border: 1px solid ${theme.app.borderColor};
    border-radius: 4px;
`;

const AttributePreviewCardOptionHeader = styled.div`
    display: flex;
    align-items: center;
    padding: 8px;
    background-color: ${theme.app.backgroundColor};
    cursor: pointer;
`;

const AttributePreviewCardOptionName = styled.div`
    flex-grow: 1;
    margin-left: 8px;
`;

const AttributePreviewCardOptionApply = styled.div`
    cursor: pointer;
    color: ${theme.app.primary};
`;

const AttributePreviewCardOptionActions = styled.div<{ isOpen: boolean }>`
    max-height: ${({ isOpen }) => (isOpen ? '300px' : '0')};
    overflow: hidden;
    transition: all 0.3s ease;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    padding: ${({ isOpen }) => (isOpen ? '8px' : '0')};
`;

const AttributePreviewCardOptionAction = styled.div`
    padding: 4px;
    margin-bottom: 4px;
    font-size: 12px;
    background-color: ${theme.app.backgroundColorBase};
    border-radius: 2px;
`;

export function AttributePreviewCard(props: AttributePreviewCardProps) {
    const [open, setOpen] = React.useState(true);
    const [options, setOptions] = React.useState(props.optionsList);
    const [selected, setSelected] = React.useState(props.selected);

    const getActionText = (action: AttributePreviewAction) => {
        return `${action.type}: ${action.objects.join(', ')}`;
    };

    const updateOption = () => {
        setOptions([...options]);
    };

    return (
        <AttributePreviewCardContainer $selected={selected}>
            <AttributePreviewCardSelectWrapper>
                {T._('Import', 'Admin')}
                <Checkbox
                    checked={selected}
                    onChange={() => {
                        setSelected(!selected);
                        props.onSelected?.(!selected);
                    }}
                />
            </AttributePreviewCardSelectWrapper>
            <AttributePreviewCardHeader onClick={() => setOpen(!open)}>
                <Icon>
                    {open ? (
                        <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'>
                            <path d='M12 8l-6 6h12l-6-6z' />
                        </svg>
                    ) : (
                        <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'>
                            <path d='M12 16l6-6H6l6 6z' />
                        </svg>
                    )}
                </Icon>
                <AttributePreviewCardTitle>{props.attributeTitle}</AttributePreviewCardTitle>
            </AttributePreviewCardHeader>
            <AttributePreviewCardOptions isOpen={open}>
                {options.map((option, index) => (
                    <AttributePreviewCardOption key={index}>
                        <AttributePreviewCardOptionHeader
                            onClick={() => {
                                option.open = !option.open;
                                updateOption();
                            }}
                        >
                            <Icon>
                                {option.open ? (
                                    <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'>
                                        <path d='M12 8l-6 6h12l-6-6z' />
                                    </svg>
                                ) : (
                                    <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'>
                                        <path d='M12 16l6-6H6l6 6z' />
                                    </svg>
                                )}
                            </Icon>
                            <AttributePreviewCardOptionName>{option.name}</AttributePreviewCardOptionName>
                            {option.actions.length > 0 && (
                                <AttributePreviewCardOptionApply
                                    onClick={(e) => {
                                        e.stopPropagation();

                                        const button = e.currentTarget;
                                        button.style.transition = 'color 0.2s ease-in-out';
                                        button.style.color = theme.app.secondary;

                                        setTimeout(() => {
                                            button.style.color = theme.app.primary;
                                            button.style.transition = '';
                                        }, 300);

                                        props.onOptionClick?.(option);
                                        e.preventDefault();
                                    }}
                                >
                                    <Icon>
                                        <Play />
                                    </Icon>
                                </AttributePreviewCardOptionApply>
                            )}
                        </AttributePreviewCardOptionHeader>
                        <AttributePreviewCardOptionActions isOpen={!!option.open}>
                            {option.actions.length > 0 ? (
                                option.actions.map((action, index) => (
                                    <AttributePreviewCardOptionAction key={index}>{getActionText(action)}</AttributePreviewCardOptionAction>
                                ))
                            ) : (
                                <div>{T._('No ACTION defined', 'Admin')}</div>
                            )}
                        </AttributePreviewCardOptionActions>
                    </AttributePreviewCardOption>
                ))}
            </AttributePreviewCardOptions>
        </AttributePreviewCardContainer>
    );
}
const StepHeader = styled.div<{ $isActive: boolean; $isCompleted: boolean; $isDisabled: boolean }>`
    display: flex;
    padding: 12px 16px;
    background-color: ${({ $isActive, $isCompleted }) => ($isActive ? '#f0f0f0ff' : $isCompleted ? '#f0f0f0ff' : '#ffffff')};
    border: 1px solid ${theme.app.borderColor};
    border-radius: 4px;
    margin-bottom: 2px;
    cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ $isDisabled }) => ($isDisabled ? theme.app.disabledOpacity : 1)};
`;
const StepNumber = styled.div<{ $isActive: boolean; $isCompleted: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: ${({ $isActive, $isCompleted }) => ($isCompleted ? theme.app.secondary : $isActive ? theme.app.primary : '#9e9e9e')};
    color: white;
    margin-right: 12px;
    font-size: 14px;
    font-weight: bold;
`;

const StepTitle = styled.div`
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 16px;
    flex-grow: 1;
`;

const StepContent = styled.div<{ isOpen: boolean }>`
    padding: ${({ isOpen }) => (isOpen ? '16px' : '0')};
    max-height: ${({ isOpen }) => (isOpen ? '500px' : '0')};
    overflow: hidden;
    transition: all 0.3s ease;

    border: 1px solid ${theme.app.borderColor};
    border-top: none;
    border-radius: 0 0 4px 4px;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
`;

const ComposerGenerateAIAttributePreview = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
    padding: 16px;

    & > div {
        flex-grow: 1;
        width: 100%;
        height: 400px;
    }
`;

const ComposerGenerateAttributeList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    height: 100%;
    justify-content: flex-start;
    align-items: flex-start;
    overflow: hidden;
    overflow-y: auto;

    & > div {
        width: 100%;
    }
`;

const CollapsibleComponent: React.FC<{ title: string; active: boolean; disabled: boolean; onToggle: (state: boolean) => void; children?: JSX.Element }> = ({
    title,
    active,
    disabled,
    onToggle,
    children
}) => {
    const [isOpenState, setIsOpenState] = React.useState(false);
    const openContent = () => {
        setIsOpenState(!isOpenState);
        onToggle(isOpenState);
    };
    return (
        <StepContainer>
            <StepHeader $isActive={active} $isCompleted={false} $isDisabled={disabled} onClick={openContent}>
                {/* <StepNumber isActive={active} isCompleted={false}>
				</StepNumber> */}
                <StepTitle>{title}</StepTitle>
            </StepHeader>
            <StepContent isOpen={isOpenState}>{children}</StepContent>
        </StepContainer>
    );
};

const ComposerGenerateAIAttributeDialog: React.FC<ComposerGenerateAIAttributeDialogProps> = ({ meshList, materialList, onConfirm, onClose, sceneEditor }) => {
    const Environment = useRelayEnvironment();

    const aiLimited = useLazyLoadQuery<composerQueriesCheckAiLimitQuery>(composerCheckAiLimitQuery, {}, { fetchPolicy: 'network-only' });
    const [currentStep, setCurrentStep] = React.useState<ComposerGenerateAttributeSteps>(ComposerGenerateAttributeSteps.Initial);
    const [promptValue, setPromptValue] = React.useState<string>('');
    const [attributes, setAttributes] = React.useState<AttributePreviewItems[]>([]);
    const isPromptInvalid = React.useMemo(() => promptValue.trim() === '', [promptValue]);

    const materialsList = (materialList ?? []).map((item) => ({ id: item.id, name: item.name })) ?? [];

    const meshesList = (meshList ?? []).map((item) => ({ id: item.id, name: item.name, parentID: item.parent?.id ?? null })) ?? [];

    if (!aiLimited) {
        return <Loader />;
    }

    if (aiLimited.checkAILimited) {
        return (
            <Dialog windowDecorator={ComposerGenerateAIAttributeDialogWindow} buttons={[{ label: T._('Ok', 'Admin'), onClick: onClose }]}>
                <NotificationBanner
                    title={T._('AI Limit Reached', 'Admin')}
                    type='CRITICAL'
                    content={T._(
                        "We're sorry, but it appears you have exceeded the allowed limit. It's important to adhere to the guidelines and limits set to ensure fair and safe usage of our services. If you need more info please contact support.",
                        'Admin'
                    )}
                />
            </Dialog>
        );
    }
    const proceedToGenerate = async () => {
        setCurrentStep(ComposerGenerateAttributeSteps.WaitingGeneration);
        const queryResult = await fetchQuery<composerQueriesGenerateAiAttributesQuery>(
            Environment,
            composerGenerateAiAttributes,
            {
                input: {
                    prompt: promptValue,
                    materials: materialList,
                    meshes: meshesList
                }
            },
            { fetchPolicy: 'network-only' }
        ).toPromise();
        if (queryResult?.generatedAIAttributes?.attributes?.length === 0) {
            setCurrentStep(ComposerGenerateAttributeSteps.Initial);
            showErrorNotification({
                message: T._('No attributes generated. Please try a different prompt.', 'Admin')
            });
            return;
        }
        let attributes: AttributePreviewItems[] =
            queryResult?.generatedAIAttributes?.attributes?.map(
                (attr) =>
                    ({
                        name: attr?.an ?? '',
                        selected: true,
                        options: attr?.o?.map(
                            (option) =>
                                ({
                                    name: option?.on ?? '',
                                    actions:
                                        option?.a?.map((action) => ({
                                            material: action?.mat,
                                            type: getCapitalizedFormattedName(action!.t ?? ''),
                                            objects: action?.m?.map((x) => meshesList.find((m) => m.id.startsWith(x ?? ''))?.name ?? x)
                                        })) ?? []
                                } as AttributePreviewOption)
                        )
                    } as AttributePreviewItems)
            ) ?? [];
        console.log('Generated attributes:', attributes);
        // remove invalid attributes
        //remove attribute with invalid options and actions
        attributes.forEach((attr) => {
            attr.options = attr.options.filter((option) => {
                option.actions = option.actions.filter((action) => action.material && action.type && action.objects.length > 0);
                return option.actions.length > 0;
            });
            return attr.options.length > 0;
        });
        attributes = attributes.filter((attr) => attr.name.trim() !== '' && attr.options.length > 0);
        console.log('Filtered attributes:', attributes);

        // check if attributes are valid
        if (attributes.length === 0) {
            setCurrentStep(ComposerGenerateAttributeSteps.Initial);
            showErrorNotification({
                message: T._('Attributes generated invalid, try again with a different prompt.', 'Admin')
            });
            return;
        }
        setAttributes(attributes);
        setCurrentStep(ComposerGenerateAttributeSteps.ComposerPreview);
    };
    if (currentStep === ComposerGenerateAttributeSteps.Initial) {
        return (
            <Dialog
                windowDecorator={ComposerGenerateAIAttributeDialogWindow}
                buttons={[{ label: T._('Generate', 'Admin'), onClick: proceedToGenerate, disabled: isPromptInvalid }]}
            >
                <Title>{T._('Generate attributes and options with AI', 'Admin')}</Title>
                <Container>
                    <NotificationBanner
                        title={T._('Please note ', 'Admin')}
                        type='WARNING'
                        content={T._('This new feature is currently in technical preview. You are limited to 15 generated output per month.', 'Admin')}
                    />
                    <PromptAiArea
                        currentPromptValue={promptValue}
                        onMicrophoneError={(error) => {
                            setPromptValue('');
                            showErrorNotification({
                                message: T._(`Microphone error occurred: ${error.message}`, 'Admin')
                            });
                        }}
                        onMicrophoneNoValue={() => {
                            setPromptValue('');
                        }}
                        onTypePrompSwitch={(isText) => {
                            setPromptValue('');
                        }}
                        onValueChange={(value) => {
                            setPromptValue(value);
                        }}
                    />
                    <CollapsibleComponent
                        title={T._('How to use AI to generate attributes and options', 'Admin')}
                        active={true}
                        disabled={false}
                        onToggle={() => {
                            // Toggle logic if needed
                        }}
                    >
                        <ul>
                            <li>
                                {T._(
                                    'Be descriptive in naming 3D scene elements—avoid codes or placeholders. Instead of "Mesh_01", use clear names like "body".',
                                    'Admin'
                                )}
                            </li>
                            <br />
                            <li>
                                {T._(
                                    'Clearly articulate your desired outcome when providing prompts. For instance, specify details like "a chair with or without armrests, with customizable body color".',
                                    'Admin'
                                )}
                            </li>
                            <br />
                            <li>
                                {T._(
                                    "Opt for descriptive group names to streamline attributes. For instance: Rather than individually listing materials (e.g., plastic, wood...), categorize them broadly, such as materials, colors, and patterns; Zakeke's AI will grasp these categories, eliminating the need for exhaustive itemization.",
                                    'Admin'
                                )}
                            </li>

                            <br />
                            <li>
                                {T._('We suggest to read our ', 'Admin')}
                                <a target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/360009576134-Composer#h_01G0M8N3Y70A56DCVA6ND2JRFF)'>
                                    {T._('full guide', 'Admin')}
                                </a>
                                {T._(' on how to use the AI to generate attributes and options.', 'Admin')}
                            </li>
                        </ul>
                    </CollapsibleComponent>
                </Container>
            </Dialog>
        );
    } else if (currentStep === ComposerGenerateAttributeSteps.ComposerPreview) {
        const onTryAgain = () => {
            setAttributes([]);
            setCurrentStep(ComposerGenerateAttributeSteps.Initial);
        };

        const onConfirmGeneration = () => {
            const selectedAttributes = attributes.filter((attr) => attr.selected);
            const nodeAttributes = selectedAttributes?.map(ComposerAttributeNodeAdapter.toNodeFromAiAttribute) ?? [];
            onConfirm(nodeAttributes);
        };

        const executeAction = async (option: AttributePreviewOption) => {
            const scene = sceneEditor?.scene;
            if (!sceneEditor || !scene) return;
            let actions: MPlaza.AttributeSceneAction[] = [];
            for (let act of option.actions) {
                let actionType = 1; // SetMeshMaterial
                let value = '';
                const action = new MPlaza.AttributeSceneAction();
                const type = act.type.replace(/ /g, '_').toLowerCase();
                if (type === 'show_object') {
                    actionType = 2; // ShowObject
                    value = 'show';
                } else if (type === 'hide_object') {
                    actionType = 2; // ShowObject
                    value = 'hide';
                } else if (type == 'set_object_material') {
                    actionType = 3; // SetMeshMaterial
                    value = scene.materials.find((x) => x.name === act.material)?.id ?? '';
                } else if (type == 'set_material_color') {
                    actionType = 4; // SetMaterialColor
                    value = act.material;
                    action.set('sceneElementTypeID', MPlaza.SceneElementType.Material);
                    action.set('propertyName', ComposerMaterialsColorType.Albedo);
                }
                action.tempID = Zakeke.generateUUID();
                action.set('actionTypeID', actionType);
                action.set(
                    'selector',
                    act.objects
                        .map((meshName) => {
                            // Try to get the ID else fallback to name
                            let mesh = meshesList.find((x) => x.name === meshName);

                            if (!mesh) {
                                // The id in meshName is only the first part of the guid
                                mesh = meshesList.find((x) => x.id.startsWith(meshName));
                            }

                            return mesh?.id ?? meshName;
                        })
                        .join(';')
                );
                if (type == 'set_material_color') {
                    action.set(
                        'selector',
                        act.objects
                            .map((materialName) => {
                                // Try to get the ID else fallback to name
                                let material = materialsList.find((x) => x.name === materialName);

                                if (!material) {
                                    // The id in meshName is only the first part of the guid
                                    material = materialsList.find((x) => x.id.startsWith(materialName));
                                }

                                return material?.id ?? materialName;
                            })
                            .join(';')
                    );
                }
                action!.set('value', value);
                actions.push(action);
            }
            console.log('Executing actions', actions);
            await sceneEditor?.executeActionsAsync(null, null, actions);
        };

        return (
            <Dialog
                windowDecorator={ComposerGenerateAIAttributeDialogWindow}
                buttons={[
                    { label: T._('Try Again', 'Admin'), outline: true, onClick: onTryAgain },
                    {
                        label: T._('Confirm', 'Admin'),
                        onClick: () => onConfirmGeneration(),
                        disabled: attributes.length === 0 || attributes.every((attr) => !attr.selected)
                    }
                ]}
            >
                <Title>{T._('AI Generated Attributes Preview', 'Admin')}</Title>
                <Container>
                    {attributes.length < 1 ? (
                        <CenteredContent>
                            {T._('No AI-generated attributes available for preview. Please try again, changing your input or settings.', 'Admin')}
                        </CenteredContent>
                    ) : (
                        <FormControl
                            caption={
                                <>
                                    {T._('Here below the list of AI-generated', 'Admin')}
                                    <a href='https://zakeke.zendesk.com/hc/en-us/articles/12934839221020-Attributes-and-Options' target='_blank'>
                                        {T._(' attributes, options and actions', 'Admin')}
                                    </a>
                                </>
                            }
                        >
                            <ComposerGenerateAIAttributePreview>
                                <ComposerGenerateAttributeList>
                                    {attributes.map((attribute) => (
                                        <AttributePreviewCard
                                            attributeTitle={attribute.name}
                                            selected={attribute.selected}
                                            optionsList={attribute.options}
                                            onOptionClick={executeAction}
                                        />
                                    ))}
                                </ComposerGenerateAttributeList>
                                <div>
                                    <ComposerPreview />
                                </div>
                            </ComposerGenerateAIAttributePreview>
                        </FormControl>
                    )}
                </Container>
            </Dialog>
        );
    } else
        return (
            <Dialog windowDecorator={ComposerGenerateAIAttributeDialogWindow} buttons={[]}>
                <CenteredContent>
                    <Loader />
                    <span>{T._("Magic is happening... This may take a few moments. Please don't leave the page!", 'Admin')}</span>
                </CenteredContent>
            </Dialog>
        );
};

export default ComposerGenerateAIAttributeDialog;
